import { createChartOption } from 'features/Integrations/features/ConnectedDevices/utils/chart.utils';
import { buildColorMap } from '../../DeviceGraph/utils/deviceGraphHelpers.utils';
import { isBuoyDevice } from '../utils/buoyDevice.utils';

const BUOY_FIELDS = [
  {
    value: 'wind_speed',
    label: 'Wind Speed',
    unitLabel: (unitType) => (unitType ? 'km/h' : 'MPH'),
    convert: (value, unitType) => (unitType ? value * 1.60934 : value),
    color: '#2f6fed',
    type: 'line',
  },
  {
    value: 'wave_height',
    label: 'Wave Height',
    unitLabel: (unitType) => (unitType ? 'm' : 'ft'),
    convert: (value, unitType) => (unitType ? value * 0.3048 : value),
    color: '#00a6a6',
    type: 'line',
  },
  {
    value: 'air_temperature',
    label: 'Air Temp',
    unitLabel: (unitType) => (unitType ? 'C' : 'F'),
    convert: (value, unitType) => (unitType ? ((value - 32) * 5) / 9 : value),
    color: '#ff7a59',
    type: 'line',
  },
  {
    value: 'dew_point',
    label: 'Dew Point',
    unitLabel: (unitType) => (unitType ? 'C' : 'F'),
    convert: (value, unitType) => (unitType ? ((value - 32) * 5) / 9 : value),
    color: '#8a63d2',
    type: 'line',
  },
  {
    value: 'water_temperature',
    label: 'Water Temp',
    unitLabel: (unitType) => (unitType ? 'C' : 'F'),
    convert: (value, unitType) => (unitType ? ((value - 32) * 5) / 9 : value),
    color: '#1aa3ff',
    type: 'line',
  },
  {
    value: 'wave_period',
    label: 'Wave Period',
    unitLabel: () => 'sec',
    convert: (value) => value,
    color: '#00b894',
    type: 'bar',
  },
  {
    value: 'baro_pressure',
    label: 'Barometric Pressure',
    unitLabel: (unitType) => (unitType ? 'mbar' : 'inHg'),
    convert: (value, unitType) => (unitType ? value * 33.8639 : value),
    color: '#495057',
    type: 'line',
  },
  {
    value: 'visibility',
    label: 'Visibility',
    unitLabel: (unitType) => (unitType ? 'km' : 'mi'),
    convert: (value, unitType) => (unitType ? value * 1.60934 : value),
    color: '#f4b400',
    type: 'line',
  },
];

const emptyChart = { labels: [], datasets: [] };

const toFiniteNumber = (value) => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const getConvertedBuoyValue = (field, value, unitType = false) => {
  const numericValue = toFiniteNumber(value);
  if (numericValue == null) return null;

  return toFiniteNumber(field.convert(numericValue, unitType));
};

export const getBuoyDevices = (devices = []) => devices.filter((device) => isBuoyDevice(device));

export const getSelectedBuoyDevices = (devices = [], selectedDeviceMap = {}) =>
  getBuoyDevices(devices).filter((device) => selectedDeviceMap?.[String(device.id)]);

export const getBuoyFieldConfig = (selectedDataType = 'wave_height') =>
  BUOY_FIELDS.find((field) => field.value === selectedDataType) || BUOY_FIELDS[0];

export const getBuoyFilterDropdownOptions = () =>
  BUOY_FIELDS.map((field) => (
    <option key={field.value} value={field.value}>
      {field.label}
    </option>
  ));

export const getBuoyYAxisLabel = (selectedDataType, unitType = false) => {
  const field = getBuoyFieldConfig(selectedDataType);
  return `${field.label} (${field.unitLabel(unitType)})`;
};

const hasBuoyPointInRange = (point, startTime, endTime) =>
  point?.ts != null && point.ts >= startTime && point.ts <= endTime;

export const buoyFieldHasData = ({
  devices = [],
  selectedDeviceMap = {},
  selectedDataType = 'wave_height',
  unitType = false,
  startTime,
  endTime,
}) => {
  if (!startTime || !endTime) return false;

  const field = getBuoyFieldConfig(selectedDataType);
  const selectedBuoyDevices = getSelectedBuoyDevices(devices, selectedDeviceMap);

  return selectedBuoyDevices.some((device) =>
    (device.data || []).some((point) => {
      if (!hasBuoyPointInRange(point, startTime, endTime)) return false;
      return getConvertedBuoyValue(field, point?.[field.value], unitType) != null;
    }),
  );
};

export const getAvailableBuoyFields = ({
  devices = [],
  selectedDeviceMap = {},
  unitType = false,
  startTime,
  endTime,
}) =>
  BUOY_FIELDS.filter((field) =>
    buoyFieldHasData({
      devices,
      selectedDeviceMap,
      selectedDataType: field.value,
      unitType,
      startTime,
      endTime,
    }),
  );

export const getFirstAvailableBuoyField = (args) => getAvailableBuoyFields(args)[0] || null;

export const buildBuoyDeviceChartModel = ({
  devices = [],
  selectedDeviceMap = {},
  selectedDataType = 'wave_height',
  unitType = false,
  startTime,
  endTime,
}) => {
  if (!devices.length || !startTime || !endTime) return emptyChart;

  const buoyDevices = getBuoyDevices(devices);
  if (!buoyDevices.length) return emptyChart;

  const field = getBuoyFieldConfig(selectedDataType);
  const colors = buildColorMap(buoyDevices);

  const datasets = Object.keys(selectedDeviceMap || {})
    .filter((deviceId) => selectedDeviceMap[deviceId])
    .map((deviceId) => buoyDevices.find((device) => String(device.id) === String(deviceId)))
    .filter(Boolean)
    .map((device, index) => {
      const filteredData = (device.data || [])
        .filter((point) => point?.ts >= startTime && point?.ts <= endTime)
        .sort((left, right) => left.ts - right.ts);

      if (!filteredData.length) return null;

      const datapoints = filteredData
        .map((point) => {
          const sourceValue = point?.[field.value];
          if (sourceValue == null) return null;
          const convertedValue = getConvertedBuoyValue(field, sourceValue, unitType);
          return convertedValue == null ? null : [point.ts, convertedValue];
        })
        .filter(Boolean);

      if (!datapoints.length) return null;

      // Build a timestamp -> data point map for event resolution
      const byTimestampMap = new Map();
      filteredData.forEach((p) => {
        if (p?.ts != null) byTimestampMap.set(p.ts, p);
      });

      return {
        _meta: {
          ...device,
          data: filteredData,
          byTimestampMap,
        },
        label: `${device.static_id ? `${device.static_id} ` : ''}${device.alias}`,
        data: datapoints,
        color: colors[device.id],
        seriesType: field.type,
        order: index,
      };
    })
    .filter(Boolean);

  if (!datasets.length) return emptyChart;

  const labels = Array.from(
    new Set(
      datasets
        .flatMap((dataset) => dataset.data.map((point) => point[0]))
        .filter((value) => value != null),
    ),
  ).sort((left, right) => left - right);

  return { labels, datasets };
};

export const buildBuoyTooltipFormatter = (datasets, selectedDataType, unitType = false) => {
  const field = getBuoyFieldConfig(selectedDataType);

  return (params) => {
    const items = Array.isArray(params) ? params : [params];
    if (!items.length) return null;

    const ts = items[0].axisValue ?? (Array.isArray(items[0].value) ? items[0].value[0] : null);
    const header = ts ? new Date(ts).toLocaleString() : '';
    const lines = [`<strong>${header}</strong>`];

    items.forEach((pointParam) => {
      const dataset = datasets[pointParam.seriesIndex];
      const raw = dataset?._meta?.data?.find((entry) => entry.ts === ts);
      if (!raw) return;

      // Main value for this series
      const selectedValue = raw?.[field.value];
      const convertedValue = getConvertedBuoyValue(field, selectedValue, unitType);
      if (convertedValue == null || Number.isNaN(convertedValue)) return; // skip if null or NaN

      lines.push(`${pointParam.marker}${dataset.label}`);
      lines.push(`${field.label}: ${convertedValue.toFixed(2)} ${field.unitLabel(unitType)}`);

      // Wind Direction
      if (
        raw.wind_direction != null &&
        raw.wind_direction !== '' &&
        !Number.isNaN(Number(raw.wind_direction))
      ) {
        lines.push(`Wind Direction: ${raw.wind_direction}`);
      }

      // Wave Height
      if (raw.wave_height != null && !Number.isNaN(Number(raw.wave_height))) {
        lines.push(
          `Wave Height: ${Number(raw.wave_height).toFixed(2)} ${getBuoyFieldConfig('wave_height').unitLabel(false)}`,
        );
      }

      // Water Temp
      if (raw.water_temperature != null && !Number.isNaN(Number(raw.water_temperature))) {
        lines.push(
          `Water Temp: ${Number(raw.water_temperature).toFixed(2)} ${getBuoyFieldConfig('water_temperature').unitLabel(false)}`,
        );
      }

      // Visibility
      if (raw.visibility != null && !Number.isNaN(Number(raw.visibility))) {
        lines.push(
          `Visibility: ${Number(raw.visibility).toFixed(2)} ${getBuoyFieldConfig('visibility').unitLabel(false)}`,
        );
      }
    });

    return `<div class="echart-tooltip-content">${lines.join('<br/>')}</div>`;
  };
};

export const buildBuoySeriesFromDatasets = (datasets, selectedDataType) => {
  const field = getBuoyFieldConfig(selectedDataType);
  return datasets.map((dataset) => ({
    name: dataset.label,
    type: field.type,
    data: dataset.data,
    showSymbol: false,
    connectNulls: true,
    smooth: false,
    itemStyle: {
      color: dataset.color,
    },
    lineStyle: {
      width: 2,
      color: dataset.color,
    },
    ...(field.type === 'line'
      ? {}
      : {
          barMaxWidth: 18,
        }),
  }));
};

export const buildBuoyChartOption = ({
  title = 'Buoy Devices',
  labels = [],
  datasets = [],
  selectedDataType,
  unitType,
  startTime,
  endTime,
}) => {
  if (!datasets.length) return null;

  const series = buildBuoySeriesFromDatasets(datasets, selectedDataType);
  const tooltipFormatter = buildBuoyTooltipFormatter(datasets, selectedDataType, unitType);

  const xAxisLabelFormatter = (value) =>
    new Intl.DateTimeFormat([], {
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
      .format(new Date(value))
      .replace(', ', ' ');

  return createChartOption({
    title,
    titleTop: 8,
    xAxisLabels: labels,
    minValue: startTime,
    maxValue: endTime,
    series,
    gridPadding: {
      left: '10%',
      right: '2%',
      top: '26%',
      bottom: '18%',
    },
    tooltipFormatter,
    xAxisRotate: 0,
    xAxisLabelFontSize: 10,
    xAxisLabelHideOverlap: true,
    xAxisLabelInterval: 'auto',
    xAxisLabelType: 'time',
    xAxisLabelFormatter,
    dataZoomEnabled: true,
    yAxisLabel: getBuoyYAxisLabel(selectedDataType, unitType),
  });
};

export const resolveBuoyObservationIdFromParams = (params, datasetMeta) => {
  if (!params || !datasetMeta) return null;
  try {
    const timestampRaw = params.axisValue ?? (Array.isArray(params.value) ? params.value[0] : null);
    const timestampNum = toFiniteNumber(timestampRaw);
    if (timestampNum == null) return null;

    const byTimestampMap = datasetMeta?.byTimestampMap;
    if (byTimestampMap) {
      let raw =
        byTimestampMap.get(timestampNum) ||
        byTimestampMap.get(String(timestampNum)) ||
        byTimestampMap.get(String(timestampRaw));
      if (!raw && Array.isArray(datasetMeta?.data)) {
        raw = datasetMeta.data.find((p) => toFiniteNumber(p?.ts) === timestampNum);
      }
      return raw?.observation_id ?? null;
    }
  } catch (e) {
    // ignore
  }
  return null;
};
